import { HttpClient, provideHttpClient } from '@angular/common/http';
import {
    ANIMATION_MODULE_TYPE,
    inject,
    Injectable,
    LOCALE_ID,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withPreloading } from '@angular/router';
import {
    provideTranslateLoader,
    provideTranslateService,
    TranslateLoader,
    TranslateService,
} from '@ngx-translate/core';
import { catchError, firstValueFrom, Observable, of, shareReplay, Subject, tap } from 'rxjs';

import { DayjsDateAdapter, MAT_DAYJS_DATE_FORMATS } from './app/helpers';

import { App } from 'app/app';
import { AppPreloader } from 'app/app-preloader';
import { AppRedirectResolver } from 'app/app-redirect-resolver';
import { ROUTES } from 'app/app.routes';
import { CURRENCY_LOCALE } from 'app/constants';
import { provideFactories } from 'app/factories/factories.providers';
import { provideGraphQL } from 'app/graphql/graphql.providers';
import { CustomPaginatorIntl } from 'app/helpers';
import { getOauthProviders } from 'app/helpers/config';
import { provideInterceptors } from 'app/http-interceptors/http-interceptors.providers';
import { AppConfig, AppConfigWindow } from 'app/interfaces';
import { OAUTH_CONFIG, OAuthConfigService } from 'app/services/auth/oauth-config.service';
import { OauthService } from 'app/services/auth/oauth.service';
import { provideServices } from 'app/services/services.providers';
import { LoadingBarService } from 'app/shared/loading-bar';
import { ModalService } from 'app/shared/modal';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
class TranslationUpdateService {
    private subject = new Subject<{ lang: string; data: Record<string, string> }>();
    updates$ = this.subject.asObservable();

    emit(lang: string, data: Record<string, string>) {
        this.subject.next({ lang, data });
    }
}

class CachingTranslateLoader implements TranslateLoader {
    private http = inject(HttpClient);
    private updateService = inject(TranslationUpdateService);
    private suffix: string = '.json';

    getTranslation(lang: string): Observable<Record<string, string>> {
        const localKey = `translations-${lang}`;
        const cachedData = localStorage.getItem(localKey);
        const prefix = environment.translationsUrl;

        const translationsUrl = `${prefix}${lang}${this.suffix}`;

        const http$ = this.http.get<Record<string, string>>(translationsUrl).pipe(
            tap((data) => {
                localStorage.setItem(localKey, JSON.stringify(data));
                this.updateService.emit(lang, data);
            }),
            catchError((err) => {
                console.warn(`Could not fetch translations for ${lang}`, err);
                return of({});
            }),
            shareReplay(1),
        );

        if (cachedData) {
            return of(JSON.parse(cachedData)).pipe(tap(() => http$.subscribe()));
        } else {
            return http$;
        }
    }
}

async function loadConfig(http: HttpClient) {
    try {
        const response = await fetch('/assets/config.json', { method: 'GET', mode: 'no-cors' });

        if (response.ok) {
            const config = await firstValueFrom(
                http.get<AppConfig>('/assets/config.json').pipe(
                    catchError(() => {
                        return of({ version: 'dev' });
                    }),
                ),
            );
            (window as AppConfigWindow).appConfig = config;
        } else {
            throw new Error('config.json not found');
        }
    } catch (error) {
        (window as AppConfigWindow).appConfig = { version: 'dev' };
    }
}

bootstrapApplication(App, {
    providers: [
        // ngx-intl-tel-input's country dropdown (ngx-bootstrap) injects `AnimationBuilder`,
        // whose default implementation throws NG03600 unless an animation module is declared.
        // Native `animate.enter`/`animate.leave` replaced `@angular/animations`, so we mark the
        // module type instead of re-adding the deprecated `provideAnimationsAsync()`.
        provideZoneChangeDetection({ eventCoalescing: true }),
        { provide: ANIMATION_MODULE_TYPE, useValue: 'BrowserAnimations' },
        ...provideServices(),
        ...provideFactories(),
        {
            provide: OAUTH_CONFIG,
            useValue: {
                providers: getOauthProviders(),
                baseUrl: environment.apiUrl,
                withCredentials: true,
            },
        },
        OAuthConfigService,
        OauthService,
        ...provideGraphQL(),
        provideRouter(ROUTES, withPreloading(AppPreloader), withComponentInputBinding()),
        LoadingBarService,
        ModalService,
        AppPreloader,
        provideTranslateService({
            loader: provideTranslateLoader(() => new CachingTranslateLoader()),
        }),
        { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
        {
            provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
            useValue: { autoActiveFirstOption: true },
        },
        {
            provide: DateAdapter,
            useClass: DayjsDateAdapter,
            deps: [MAT_DATE_LOCALE],
        },
        { provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS },
        {
            provide: LOCALE_ID,
            useValue: CURRENCY_LOCALE,
        },
        provideAppInitializer(() => {
            const initializerFn = (
                (http: HttpClient) => () =>
                    loadConfig(http)
            )(inject(HttpClient));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = ((updateService: TranslationUpdateService, translate: TranslateService) => {
                return () => {
                    updateService.updates$.subscribe(({ lang, data }) => {
                        translate.setTranslation(lang, data, true);
                    });
                };
            })(inject(TranslationUpdateService), inject(TranslateService));
            return initializerFn();
        }),
        AppRedirectResolver,
        provideHttpClient(provideInterceptors()),
    ],
}).catch((err) => console.error(err));
